/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';

import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { PADDINGS, COLORS2_AS_BACKGROUND } from '^/constants/commonStyles';
import { ReservationDetailed } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';

import { HoverToReveal } from '../../../../commonComponents/ui/HoverToReveal';
import { AdditionalFees2 } from '../../AdditionalFees2';
import { PPDOnsitePaymentHoverMessage } from '../../onsiteInfoMessages';

import {
  billDivider,
  billLabel,
  billRow,
  billSubValue,
  billTitle,
  billTotalLabel,
  billTotalValue,
  billValue,
  flexRightmostItemRow,
} from './ReservationDetailDesktop.commonStuff';
import { formatDateString } from '^/util/dateFormat';
import dayjs from 'dayjs';
import { ExtraChargeDisplayCurrency } from '../../extraChargeDisplay';
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';

// dayjs.extend(utc);
// dayjs.extend(timezone);

function isCancelledAndPPDReservation(data: ReservationDetailed) {
  if (data.res_status !== 'canceled' || data.pay_type !== 'PPD') {
    throw new Error('DummyErrorForTypeFiltering');
  }
  return data;
}

export const ReservationDetailsBillPPDCancelled: React.FC<{
  data: ReturnType<typeof isCancelledAndPPDReservation>;
  extraChargeDisplayCurrency?: ExtraChargeDisplayCurrency;
}> = observer(function ReservationDetailsBillPPDCancelled({
  data,
  extraChargeDisplayCurrency = 'foreign',
}) {
  const { t } = useTranslation();

  const paymentTime = formatDateString(data.date_request, 'YYYY.MM.DD HH:mm (z)', 'Asia/Seoul');
  const cancelTime = formatDateString(data.date_cancel, 'YYYY.MM.DD HH:mm (z)', 'Asia/Seoul');
  const isKrwDisplay = extraChargeDisplayCurrency === 'krw';
  const onSiteAmount = isKrwDisplay
    ? data.extra_charge?.additional_rate_amount ?? data.extra_charge?.additional_amount
    : data.extra_charge?.additional_amount;
  const onSiteCurrencyLabel = isKrwDisplay ? t('won') : data.paymentDetails.onSiteCurrency;
  const formatForeignAmount = (value?: number | string | null) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div className="w-full ">
      <h3 className={billTitle}>요금 내역</h3>

      <div
        id="separator1"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />

      <div className={['flex flex-col gap-[10px]'].join(' ')}>
        <div className={billRow}>
          <p className={billLabel}>대여 요금</p>
          <p className={billValue}>{formatKRWWonAmount(data.rate_amount)} 원</p>
        </div>
        {data.extra_charge && (data.extra_charge.additional_amount || 0) > 0 && (
          <div className={['mt-[5px]'].join(' ')}>
            <AdditionalFees2
              extra_charge={data.extra_charge}
              onSiteCurrency={data.extra_equipments_json.currency}
              displayCurrency={extraChargeDisplayCurrency}
            />
          </div>
        )}

        <div
          id="separator"
          className={[
            billDivider,
            css`
              margin-top: 12px;
              margin-bottom: 12px;
            `,
          ].join(' ')}
        />
        <div className={billRow}>
          <span className={billTotalLabel}>즉시 결제 금액</span>

          <span className={billTotalValue}>{formatKRWWonAmount(data.paid_amount)} 원</span>
        </div>
        {!isKrwDisplay && (data.extra_charge?.additional_amount || null) && (
          <>
            <div
              className={[
                billRow,
                css`
                  position: relative;
                `,
              ].join(' ')}
            >
              <div
                className={[
                  billLabel,
                  css`
                    display: flex;
                    align-items: center;
                    justify-items: center;
                    height: fit-content;
                    gap: 6px;
                  `,
                ].join(' ')}
              >
                <span>현장 결제 예상금액</span>
                <HoverToReveal
                  toReveal={
                    <div
                      className={[
                        billSubValue,
                        PADDINGS.TOP_AND_BOTTOM.spacing16,
                        COLORS2_AS_BACKGROUND.sub.MrtGray3,
                        css`
                          position: absolute;
                          left: 0;
                          right: 0;
                          top: 100%;
                          padding-left: 10px;
                          padding-right: 10px;
                          border-radius: 10px;
                          text-align: left;
                        `,
                      ].join(' ')}
                    >
                      {PPDOnsitePaymentHoverMessage}
                    </div>
                  }
                />
              </div>

              <p className={billValue}>
                {isKrwDisplay
                  ? formatKRWWonAmount(onSiteAmount, 0.000001)
                  : formatForeignAmount(onSiteAmount)}
                &nbsp;
                {onSiteCurrencyLabel}
              </p>
            </div>
            <p className={[billSubValue, flexRightmostItemRow].join(' ')}>
              약 &nbsp;
              {formatKRWWonAmount(data.extra_charge?.additional_rate_amount)}
              &nbsp;
              {t('won')}
            </p>
          </>
        )}
      </div>

      <div
        id="separator3"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />
      <div className={billRow}>
        <p className={billLabel}>결제 수단</p>
        <p className={billValue}>신용카드</p>
      </div>

      <div className={[billRow, 'mt-[10px]'].join(' ')}>
        <p className={billLabel}>결제 일시</p>
        <p className={billValue}>{paymentTime}</p>
      </div>

      <div className={[billRow, 'mt-[10px]'].join(' ')}>
        <p className={billLabel}>취소 일시</p>
        <p className={billValue}>{cancelTime}</p>
      </div>

      <div
        id="separator2"
        className={[
          billDivider,
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
        ].join(' ')}
      />

      <div className={billRow}>
        <span className={billTotalLabel}>환불 금액</span>

        <span className={billTotalValue}>{formatKRWWonAmount(data.refund_price)} 원</span>
      </div>

      <div className={[billRow, 'mt-[10px]'].join(' ')}>
        <span className={billTotalLabel}>취소 수수료</span>
        <span className={billTotalValue}>{formatKRWWonAmount(data.cancel_fee)} 원</span>
      </div>
      <div
        className={css`
          text-align: center;
          white-space: pre-line;
          margin-top: 15px;
          font-size: 12px;
          line-height: 16px;
          color: #1e1e1e;
        `}
      >
        {`취소 시 환불은 카드사를 통해 2~5일 내 환불될 예정입니다.\n실제 적용되는 취소수수료 및 환불금액과 다르게 보일 수 있습니다.`}
      </div>
    </div>
  );
});
